// ** React Imports
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { toast, ToastContainer } from "react-toastify";

// ** Custom Component Imports
import CustomTextField from "components/mui/text-field";

// ** API Imports
import { saveDepartment } from "../logic/Departmentlogic";

const DepartmentDialog = ({ open, handleClose, DepartmentProps }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    existingDepartment,
    fetchDepartments,
    departmentContent,
    onDepartmentCreated,
  } = DepartmentProps;

  const [department, setDepartment] = useState({
    departmentID: 0,
    department: "",
    isActive: true,
  });

  const [errors, setErrors] = useState({
    department: false,
    departmentMsg: '',
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (departmentContent.entity === 'create') {
      resetDepartment();
    }

    if (departmentContent.entity === 'edit' && existingDepartment) {
      setDepartment({
        departmentID: existingDepartment.departmentID || 0,
        department: existingDepartment.department || "",
        isActive: existingDepartment.isActive !== undefined ? existingDepartment.isActive : true,
      });
    }
  }, [open, existingDepartment]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDepartment((prevDepartment) => ({
      ...prevDepartment,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateDepartment = (departmentName) => {
    if (!departmentName?.trim()) {
      return { error: true, message: 'Department Name is required' };
    }

    if (departmentName.trim().length < 2) {
      return { error: true, message: 'Department Name must be at least 2 characters long' };
    }

    return { error: false, message: '' };
  };

  const handleSave = async () => {
  const departmentError = validateDepartment(department.department);

  const newErrors = {
    department: departmentError.error,
    departmentMsg: departmentError.message,
  };

  setErrors(newErrors);

  if (departmentError.error) {
    toast.error("Please fill out all required fields correctly", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
    return;
  }

  setLoading(true);

  try {
    const result = await saveDepartment(department, navigate);
    
    if (result && !result.isError) {
      // Check if it's an edit or add operation
      const isEdit = departmentContent?.entity === 'edit';
      const message = isEdit 
        ? "Department Updated Successfully" 
        : "Department Added Successfully";
      
      toast.success(message, {
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
      });
      
      if (fetchDepartments) {
        await fetchDepartments();
      }
      
      if (onDepartmentCreated) {
        onDepartmentCreated(result);
      }
      
      resetDepartment();
      handleClose();
    } else {
      toast.error(result?.message || "Failed to save department");
    }
  } catch (error) {
    console.error("Error saving department:", error);
    toast.error("An error occurred while saving the department", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
  } finally {
    setLoading(false);
  }
};

  const resetDepartment = () => {
    setDepartment({
      departmentID: 0,
      department: "",
      isActive: true,
    });
    setErrors({
      department: false,
      departmentMsg: '',
    });
  };

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="xs"
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #D9D9D9",
            paddingBottom: "20px !important",
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {departmentContent?.title || "Add Department"}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 1 ,mb:1}}>
          <Grid container spacing={5}>
            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                required
                label="Department Name"
                name="department"
                value={department.department}
                placeholder="Enter department name"
                onChange={handleChange}
                error={errors.department}
                helperText={errors.department ? errors.departmentMsg : ''}
                disabled={loading}
              />
            </Grid>
            
            {/* <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isActive"
                    checked={department.isActive}
                    onChange={handleChange}
                    color="primary"
                    disabled={loading}
                  />
                }
                label="Active Department"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    color: theme.palette.text.primary,
                  }
                }}
              />
            </Grid> */}
          </Grid>
        </DialogContent>
        
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "20px 24px !important",
            boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
            zIndex: 1,
          }}
          className="dialog-actions-dense"
        >
          <Box>
            <Button 
              variant="contained" 
              color="info" 
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>
          <Box>
            <Button 
              variant="contained" 
              color="main" 
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? "Saving..." : (departmentContent?.entity === 'edit' ? 'Save Changes' : 'Save Department')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DepartmentDialog;